import {addDays, isValid, parseISO} from 'date-fns';
import {
  BlockerContext,
  Form,
  FormControl,
  FormField,
  FormSubmitHandler,
  NavigateWithoutBlocker,
  Option,
  Segment,
  SegmentProps,
  Separator,
} from 'platform/components';
import {Box, Grid, Heading, Hide, Show, Text, VStack} from 'platform/foundation';

import {ReactNode, useRef} from 'react';
import {UseFormReset, UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {useReadCodelistQuery} from '@dms/api/core';
import {
  CreateVehicleFinancingTemplateRequestBody,
  useListVehicleFinancingTemplateQuery,
  VehicleFinancingInterestRateType,
  VehicleFinancingPaymentStep,
  VehicleFinancingScopeMode,
  VehicleFinancingTemplateExpirationAction,
  VehicleFinancingTemplateResponseBody,
  VehicleFinancingTemplateType,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {COUNTRY_DEU, useBranches, useTenant} from '@dms/shared';

import {
  composePath,
  getApiDateString,
  Nullish,
  RequiredTestIdProps,
  suffixTestId,
  useOnMount,
} from 'shared/web';

import {RateMatrix} from '../RateMatrix/RateMatrix';
import {serializeRateMatrix} from '../RateMatrix/utils/serializeRateMatrix';
import {OneTimeCostField} from './components/OneTimeCostField';
import {VehicleFinancingTemplateFormFooterSlot} from './components/VehicleFinancingTemplateFormFooterSlot';
import {VehicleFinancingTemplateScopeFields} from './components/VehicleFinancingTemplateScopeFields';
import {
  vehicleFinancingTemplateFormSchema,
  VehicleFinancingTemplateFormValues,
} from './schemas/vehicleFinancingTemplateFormSchema';
import {VehicleFinancingTemplateFormFooterState} from './types/VehicleFinancingTemplateFormFooterState';
import {getInitialFormValues} from './utils/getInitialFormValues';
import {mapFormValuesToRequestBody} from './utils/mapFormValuesToRequestBody';
import {
  resolveTemplateMoneyCurrencies,
  TemplateMoneyCurrencies,
} from './utils/resolveTemplateMoneyCurrencies';

const LEASING_AND_CREDIT_CODELIST_ID = 'leasing_and_credit_company';
const ONE_TIME_COSTS_COLUMNS = 4;
// The settings pages render the form in a page body; the sections outside the matrix are capped
// so the inputs do not stretch across a wide screen. Embedded (dialog) mode uses the full width.
const PAGE_SECTION_MAX_WIDTH = 200;

type ValidityMode = VehicleFinancingTemplateFormValues['validityMode'];

export interface VehicleFinancingTemplateFormHelpers {
  reset: UseFormReset<VehicleFinancingTemplateFormValues>;
  blockerContext?: BlockerContext;
}

/**
 * The save/discard pair a dialog needs to drive the form from the outside (its own save bar, a
 * confirm-before-close prompt, or a panel-level pending-changes bar).
 */
export interface VehicleFinancingTemplateFormPendingEditor {
  save: () => Promise<boolean>;
  discard: VoidFunction;
}

export interface VehicleFinancingTemplateFormProps extends RequiredTestIdProps {
  // Loaded template on edit; `undefined` on create (blank defaults).
  template?: VehicleFinancingTemplateResponseBody | Nullish;
  // Set when the user lacks the UPDATE action: every input is disabled and the footer is hidden.
  isReadOnly?: boolean;
  /**
   * Renders the form for a dialog instead of a settings page: no section width cap and no router
   * unsaved-changes blocker (the host dialog owns the discard prompt).
   */
  isEmbedded?: boolean;
  /** Enables Shift-click multi-selection and bulk rate editing in the matrix. */
  isBulkEditEnabled?: boolean;
  onSubmit: (
    requestBody: CreateVehicleFinancingTemplateRequestBody,
    helpers: VehicleFinancingTemplateFormHelpers
  ) => Promise<void>;
  /** Renders the actions below the form. Omitted (or read-only) means no footer at all. */
  renderFooter?: (state: VehicleFinancingTemplateFormFooterState) => ReactNode;
  /**
   * Called on every dirty/pristine edge with the editor handle while dirty and `null` once clean.
   * Subscribed outside React's render pass, so it does not re-render the form tree.
   */
  onDirtyChange?: (editor: VehicleFinancingTemplateFormPendingEditor | null) => void;
}

export function VehicleFinancingTemplateForm(props: VehicleFinancingTemplateFormProps) {
  const {tenantCurrency, isTenantCountrySupported} = useTenant();
  const isGermanTenant = isTenantCountrySupported(COUNTRY_DEU);
  const moneyCurrencies = resolveTemplateMoneyCurrencies(props.template, tenantCurrency);

  const handleSubmit: FormSubmitHandler<VehicleFinancingTemplateFormValues> = (
    values,
    _setErrors,
    reset,
    blockerContext
  ) => {
    const serializedMatrix = serializeRateMatrix(values.rateMatrix, values.type[0], {
      term: props.template?.defaultTerm ?? null,
      downPayment: props.template?.defaultDownPayment ?? null,
    });

    return props.onSubmit(
      mapFormValuesToRequestBody(values, {
        rateMatrix: serializedMatrix.rateMatrix,
        defaultBalloonPayment: serializedMatrix.defaultBalloonPayment,
        defaultTerm: serializedMatrix.defaultTerm,
        defaultDownPayment: serializedMatrix.defaultDownPayment,
        moneyCurrencies,
      }),
      {reset, blockerContext}
    );
  };

  return (
    <Form<VehicleFinancingTemplateFormValues>
      experimentalZodSchema={vehicleFinancingTemplateFormSchema}
      defaultValues={getInitialFormValues(props.template)}
      onSubmit={handleSubmit}
      isDisabled={props.isReadOnly}
      shouldWatchForUnsavedChanges={props.isEmbedded !== true}
      data-testid={suffixTestId('form', props)}
    >
      {(control, formApi, navigateWithoutBlocker) => (
        <VehicleFinancingTemplateFormContent
          control={control}
          formApi={formApi}
          navigateWithoutBlocker={navigateWithoutBlocker}
          moneyCurrencies={moneyCurrencies}
          isReadOnly={props.isReadOnly ?? false}
          isGermanTenant={isGermanTenant}
          isEmbedded={props.isEmbedded ?? false}
          isBulkEditEnabled={props.isBulkEditEnabled ?? false}
          templateId={props.template?.id}
          renderFooter={props.renderFooter}
          onDirtyChange={props.onDirtyChange}
          data-testid={props['data-testid']}
        />
      )}
    </Form>
  );
}

interface VehicleFinancingTemplateFormContentProps extends RequiredTestIdProps {
  control: FormControl<VehicleFinancingTemplateFormValues>;
  formApi: UseFormReturn<VehicleFinancingTemplateFormValues>;
  navigateWithoutBlocker: NavigateWithoutBlocker;
  moneyCurrencies: TemplateMoneyCurrencies;
  isReadOnly: boolean;
  isGermanTenant: boolean;
  isEmbedded: boolean;
  isBulkEditEnabled: boolean;
  templateId?: string;
  renderFooter?: (state: VehicleFinancingTemplateFormFooterState) => ReactNode;
  onDirtyChange?: (editor: VehicleFinancingTemplateFormPendingEditor | null) => void;
}

function VehicleFinancingTemplateFormContent(props: VehicleFinancingTemplateFormContentProps) {
  const {branchOptions: rawBranchOptions, isLoading: isBranchesLoading} = useBranches();
  const branchOptions: Option[] = rawBranchOptions
    .filter((option) => isNotNil(option.label))
    .map((option) => ({value: option.value, label: option.label ?? ''}));
  const {data: leasingCodelist, isLoading: isCodelistLoading} = useReadCodelistQuery({
    codelistId: LEASING_AND_CREDIT_CODELIST_ID,
  });
  const {data: financingTemplates, isLoading: isTemplatesLoading} =
    useListVehicleFinancingTemplateQuery();

  const [selectedType, scopeMode, validityMode, validTo, expirationAction] = useWatch({
    control: props.control,
    name: ['type', 'scope.mode', 'validityMode', 'validTo', 'expirationAction'],
  });

  const pendingEditor: VehicleFinancingTemplateFormPendingEditor = {
    save: () => props.control.submitForm(),
    discard: () => props.formApi.reset(),
  };
  const unsubscribeRef = useRef<VoidFunction | null>(null);

  // RHF's own out-of-render subscription. Reading `formState` here instead would register a root
  // subscription and re-render the whole form tree on every dirty transition.
  useOnMount(
    () => {
      const onDirtyChange = props.onDirtyChange;

      if (onDirtyChange === undefined) {
        return;
      }

      unsubscribeRef.current = props.formApi.subscribe({
        formState: {isDirty: true},
        callback: ({isDirty}) => onDirtyChange(isDirty === true ? pendingEditor : null),
      });
    },
    () => unsubscribeRef.current?.()
  );

  const isBalloonType = selectedType[0] === 'BALLOON';
  const hasEndDate = isNotNil(validTo);
  const parsedValidTo = isNotNil(validTo) ? parseISO(validTo) : null;
  const firstDayAfterExpiration =
    isNotNil(parsedValidTo) && isValid(parsedValidTo)
      ? getApiDateString(addDays(parsedValidTo, 1))
      : null;
  const replacementTemplateOptions: Option[] =
    financingTemplates
      ?.filter(
        (template) =>
          !template.archived &&
          template.id !== props.templateId &&
          template.type === selectedType[0] &&
          isNotNil(firstDayAfterExpiration) &&
          (template.validFrom === null || template.validFrom <= firstDayAfterExpiration) &&
          (template.validTo === null || template.validTo >= firstDayAfterExpiration)
      )
      .map((template) => ({value: template.id, label: template.name})) ?? [];
  const financialInstitutionOptions: Option[] =
    leasingCodelist?.codes
      .filter((code) => !code.isDisabled)
      .map((code) => ({value: code.codeId, label: code.name})) ?? [];

  const manageInstitutionsHref = composePath(settingsRoutes.codelistDetail, {
    params: {id: LEASING_AND_CREDIT_CODELIST_ID},
  });

  const handleValidityModeChange = (mode: ValidityMode) => {
    props.formApi.setValue('validityMode', mode, {shouldDirty: true});

    if (mode === 'PERIOD') {
      return;
    }

    props.formApi.setValue('validFrom', null, {shouldDirty: true});
    props.formApi.setValue('validTo', null, {shouldDirty: true});
    props.formApi.setValue('expirationAction', null, {shouldDirty: true});
    props.formApi.setValue('replacementTemplateId', null, {shouldDirty: true});
  };

  const sectionMaxWidth = props.isEmbedded ? undefined : PAGE_SECTION_MAX_WIDTH;

  return (
    <VStack spacing={6} data-testid={suffixTestId('sections', props)}>
      <Box maxWidth={sectionMaxWidth} data-testid={suffixTestId('sectionsBeforeMatrix', props)}>
        <VStack spacing={4}>
          <Heading size={4}>{i18n.t('entity.vehicleFinancingTemplate.sections.basics')}</Heading>
          <FormField
            control={props.control}
            name="name"
            type="text"
            isRequired
            label={i18n.t('general.labels.name')}
            data-testid={suffixTestId('name', props)}
          />
          <FormField
            control={props.control}
            name="financialInstitutionCodeId"
            type="choice"
            isRequired
            label={i18n.t('entity.vehicleFinancialInstitution.labels.title')}
            options={financialInstitutionOptions}
            isLoading={isCodelistLoading}
            menuInPortal
            labelActions={[
              {
                title: i18n.t(
                  'entity.vehicleFinancingTemplate.actions.manageFinancialInstitutions'
                ),
                leftIcon: 'action/settings',
                href: manageInstitutionsHref,
              },
            ]}
            data-testid={suffixTestId('financialInstitutionCodeId', props)}
          />
          <VStack spacing={2}>
            <Text size="xSmall" color="secondary">
              {i18n.t('entity.vehicleFinancingTemplate.hints.validity')}
            </Text>
            <Segment<ValidityMode>
              label={i18n.t('entity.vehicleFinancingTemplate.labels.validityMode')}
              value={validityMode}
              isDisabled={props.isReadOnly}
              options={VALIDITY_MODE_OPTIONS}
              onChange={handleValidityModeChange}
              data-testid={suffixTestId('validityMode', props)}
            />
            <Show when={validityMode === 'PERIOD'}>
              <Grid columns={4}>
                <FormField
                  control={props.control}
                  name="validFrom"
                  type="apiDate"
                  label={i18n.t('entity.vehicleFinancingTemplate.labels.validFrom')}
                  tooltip={i18n.t('entity.vehicleFinancingTemplate.hints.validity')}
                  data-testid={suffixTestId('validFrom', props)}
                />
                <FormField
                  control={props.control}
                  name="validTo"
                  type="apiDate"
                  label={i18n.t('entity.vehicleFinancingTemplate.labels.validTo')}
                  tooltip={i18n.t('entity.vehicleFinancingTemplate.hints.validity')}
                  onChange={(value) => {
                    if (isNotNil(value)) {
                      return;
                    }

                    props.formApi.setValue('expirationAction', null, {shouldDirty: true});
                    props.formApi.setValue('replacementTemplateId', null, {shouldDirty: true});
                  }}
                  data-testid={suffixTestId('validTo', props)}
                />
              </Grid>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.vehicleFinancingTemplate.hints.campaignPeriodLifecycle')}
              </Text>
            </Show>
          </VStack>
          <Show when={validityMode === 'PERIOD' && hasEndDate}>
            <VStack spacing={3}>
              <Text size="xSmall" color="secondary">
                {i18n.t('entity.vehicleFinancingTemplate.hints.expirationPolicy')}
              </Text>
              <Grid columns={2}>
                <FormField
                  control={props.control}
                  name="expirationAction"
                  type="radio"
                  isRequired
                  label={i18n.t('entity.vehicleFinancingTemplate.labels.afterExpiration')}
                  tooltip={i18n.t('entity.vehicleFinancingTemplate.tooltips.afterExpiration')}
                  options={EXPIRATION_ACTION_OPTIONS}
                  data-testid={suffixTestId('expirationAction', props)}
                />
                <Show when={expirationAction === 'REPLACE'}>
                  <FormField
                    control={props.control}
                    name="replacementTemplateId"
                    type="choice"
                    isRequired
                    label={i18n.t('entity.vehicleFinancingTemplate.labels.replacementTemplate')}
                    tooltip={i18n.t('entity.vehicleFinancingTemplate.tooltips.replacementTemplate')}
                    options={replacementTemplateOptions}
                    isLoading={isTemplatesLoading}
                    menuInPortal
                    data-testid={suffixTestId('replacementTemplateId', props)}
                  />
                </Show>
              </Grid>
            </VStack>
          </Show>
          <Separator spacing={0} />
          <Heading size={4}>{i18n.t('entity.vehicleFinancingTemplate.sections.rateSetup')}</Heading>
          <FormField
            control={props.control}
            name="type"
            type="chips"
            isRequired
            label={i18n.t('entity.vehicleFinancingTemplate.labels.type')}
            options={TEMPLATE_TYPE_OPTIONS}
            data-testid={suffixTestId('type', props)}
          />
          <FormField
            control={props.control}
            name="interestRateType"
            type="chips"
            label={i18n.t('entity.vehicleFinancingTemplate.labels.interestRateType')}
            options={INTEREST_RATE_TYPE_OPTIONS}
            isDeselectable
            data-testid={suffixTestId('interestRateType', props)}
          />
          <FormField
            control={props.control}
            name="downPaymentStep"
            type="chips"
            isRequired
            label={i18n.t('entity.vehicleFinancingTemplate.labels.downPaymentStep')}
            options={PAYMENT_STEP_OPTIONS}
            data-testid={suffixTestId('downPaymentStep', props)}
          />
          <Show when={isBalloonType}>
            <FormField
              control={props.control}
              name="balloonPaymentStep"
              type="chips"
              isRequired
              label={i18n.t('entity.vehicleFinancingTemplate.labels.balloonPaymentStep')}
              options={PAYMENT_STEP_OPTIONS}
              data-testid={suffixTestId('balloonPaymentStep', props)}
            />
          </Show>
          <Separator spacing={0} />
        </VStack>
      </Box>

      <VStack spacing={4}>
        <Heading size={4}>{i18n.t('entity.vehicleFinancingTemplate.sections.rateMatrix')}</Heading>
        <RateMatrix
          control={props.control}
          isBalloonType={isBalloonType}
          isReadOnly={props.isReadOnly}
          isBulkEditEnabled={props.isBulkEditEnabled}
          data-testid={suffixTestId('rateMatrix', props)}
        />
      </VStack>

      <Box maxWidth={sectionMaxWidth} data-testid={suffixTestId('sectionsAfterMatrix', props)}>
        <VStack spacing={4}>
          <Separator spacing={0} />
          <Heading size={4}>
            {i18n.t('entity.vehicleFinancingTemplate.sections.oneTimeCosts')}
          </Heading>
          <Grid columns={ONE_TIME_COSTS_COLUMNS} align="flex-end">
            <FormField
              control={props.control}
              name="processingFee"
              type="currency"
              currency={props.moneyCurrencies.processingFee}
              decimalPlaces={2}
              label={i18n.t('entity.vehicleFinancingTemplate.labels.processingFee')}
              data-testid={suffixTestId('processingFee', props)}
            />
            <Show when={props.isGermanTenant}>
              <OneTimeCostField
                control={props.control}
                label={i18n.t('entity.vehicleFinancingTemplate.labels.vehicleTransferAmount')}
                checkboxAriaLabel={i18n.t(
                  'entity.vehicleFinancingTemplate.labels.vehicleTransferIncluded'
                )}
                includedName="vehicleTransferIncluded"
                amountName="vehicleTransferAmount"
                currency={props.moneyCurrencies.vehicleTransferAmount}
                data-testid={suffixTestId('vehicleTransfer', props)}
              />
              <OneTimeCostField
                control={props.control}
                label={i18n.t('entity.vehicleFinancingTemplate.labels.vehicleRegistrationAmount')}
                checkboxAriaLabel={i18n.t(
                  'entity.vehicleFinancingTemplate.labels.vehicleRegistrationIncluded'
                )}
                includedName="vehicleRegistrationIncluded"
                amountName="vehicleRegistrationAmount"
                currency={props.moneyCurrencies.vehicleRegistrationAmount}
                data-testid={suffixTestId('vehicleRegistration', props)}
              />
              <OneTimeCostField
                control={props.control}
                label={i18n.t('entity.vehicleFinancingTemplate.labels.vehicleDeliveryAmount')}
                checkboxAriaLabel={i18n.t(
                  'entity.vehicleFinancingTemplate.labels.vehicleDeliveryIncluded'
                )}
                includedName="vehicleDeliveryIncluded"
                amountName="vehicleDeliveryAmount"
                currency={props.moneyCurrencies.vehicleDeliveryAmount}
                data-testid={suffixTestId('vehicleDelivery', props)}
              />
            </Show>
          </Grid>
          <Show when={props.isGermanTenant}>
            <Separator spacing={0} />
            <Heading size={4}>
              {i18n.t('entity.vehicleFinancingTemplate.sections.standardOptions')}
            </Heading>
            <FormField
              control={props.control}
              name="tradeInPossible"
              type="checkbox"
              label={i18n.t('entity.vehicleFinancingTemplate.labels.tradeInPossible')}
              data-testid={suffixTestId('tradeInPossible', props)}
            />
            <FormField
              control={props.control}
              name="creditProtectionAutoEnable"
              type="checkbox"
              label={i18n.t('entity.vehicleFinancingTemplate.labels.creditProtectionAutoEnable')}
              data-testid={suffixTestId('creditProtectionAutoEnable', props)}
            />
          </Show>

          <Separator spacing={0} />

          <Heading size={4}>{i18n.t('entity.vehicleFinancingTemplate.sections.scope')}</Heading>
          <FormField
            control={props.control}
            name="scope.mode"
            type="radio"
            label={i18n.t('entity.vehicleFinancingTemplate.labels.scopeMode')}
            options={SCOPE_MODE_OPTIONS}
            data-testid={suffixTestId('scope-mode', props)}
          />
          <VehicleFinancingTemplateScopeFields
            control={props.control}
            formApi={props.formApi}
            mode={scopeMode}
            branchOptions={branchOptions}
            moneyCurrencies={props.moneyCurrencies}
            data-testid={props['data-testid']}
          />

          <Hide when={props.isReadOnly}>
            <VehicleFinancingTemplateFormFooterSlot
              control={props.control}
              navigateWithoutBlocker={props.navigateWithoutBlocker}
              isOptionsLoading={isBranchesLoading}
              discard={pendingEditor.discard}
              renderFooter={props.renderFooter}
            />
          </Hide>
        </VStack>
      </Box>
    </VStack>
  );
}

const VALIDITY_MODE_OPTIONS: NonNullable<SegmentProps<ValidityMode>['options']> = [
  {value: 'UNLIMITED', label: i18n.t('general.labels.unlimited')},
  {
    value: 'PERIOD',
    label: i18n.t('entity.vehicleFinancingTemplate.labels.campaignPeriod'),
    leftIcon: 'action/date_range',
  },
];

const TEMPLATE_TYPE_OPTIONS: Option<VehicleFinancingTemplateType>[] = [
  {value: 'STANDARD', label: i18n.t('entity.vehicleFinancingTemplate.type.standard')},
  {value: 'BALLOON', label: i18n.t('entity.vehicleFinancingTemplate.type.balloon')},
];

const INTEREST_RATE_TYPE_OPTIONS: Option<VehicleFinancingInterestRateType>[] = [
  {value: 'FIXED', label: i18n.t('entity.vehicleFinancingTemplate.interestRateType.fixed')},
  {value: 'VARIABLE', label: i18n.t('entity.vehicleFinancingTemplate.interestRateType.variable')},
  {value: 'COMBINED', label: i18n.t('entity.vehicleFinancingTemplate.interestRateType.combined')},
];

const PAYMENT_STEP_OPTIONS: Option<VehicleFinancingPaymentStep>[] = [
  {value: 'ONE_PERCENT', label: '1 %'},
  {value: 'FIVE_PERCENT', label: '5 %'},
  {value: 'TEN_PERCENT', label: '10 %'},
];

const EXPIRATION_ACTION_OPTIONS: Option<VehicleFinancingTemplateExpirationAction>[] = [
  {
    value: 'REMOVE',
    label: i18n.t('entity.vehicleFinancingTemplate.expirationAction.remove'),
  },
  {
    value: 'REPLACE',
    label: i18n.t('entity.vehicleFinancingTemplate.expirationAction.replace'),
  },
];

const SCOPE_MODE_OPTIONS: Option<VehicleFinancingScopeMode>[] = [
  {value: 'ALL_VEHICLES', label: i18n.t('entity.vehicleFinancingTemplate.scopeMode.allVehicles')},
  {value: 'BRANCH', label: i18n.t('entity.vehicleFinancingTemplate.scopeMode.branch')},
  {value: 'SEGMENT', label: i18n.t('entity.vehicleFinancingTemplate.scopeMode.segment')},
];
